'use client';
/**
 * Оболочка экранов входа и регистрации.
 *
 * Это первая точка входа в продукт: человек приходит со светлого лендинга,
 * и переход не должен ощущаться как переключение сайта. Поэтому здесь тот же
 * язык, что на logixa.one — светлая подложка, техническая сетка 72px,
 * вордмарк LOGI·X·A, mono-надзаголовок, оранжевый только на действии.
 *
 * Из старой версии сознательно убрано: неоновые glow-пятна, орбитальные
 * кольца, парящий грузовик, градиентный текст. Это операционный софт,
 * а не заставка.
 */
import Link from 'next/link';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cx } from '@/components/ui';

export function AuthShell({
  kicker, headline, lede, points, children,
}: {
  kicker: string;
  headline: ReactNode;
  lede?: string;
  points?: string[];
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen flex bg-[var(--background)]">
      {/* ── Левая колонка: обещание продукта ───────────────────── */}
      <aside className="hidden lg:flex lg:w-[52%] xl:w-[55%] relative items-center border-r border-[var(--border)] bg-[var(--surface)]">
        {/* Та же техническая сетка, что в hero лендинга */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(to right, var(--border) 1px, transparent 1px),' +
              'linear-gradient(to bottom, var(--border) 1px, transparent 1px)',
            backgroundSize: '72px 72px',
            opacity: 0.45,
          }}
        />
        <div className="relative px-12 xl:px-20 py-16 max-w-[560px]">
          <Link href="https://logixa.one" className="block mb-10 w-fit focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)] rounded-[var(--radius-sm)]">
            <span className="text-[23px] font-bold tracking-[-0.035em] text-[var(--text-primary)]">
              LOGI<span className="text-[var(--brand)]">X</span>A
            </span>
          </Link>

          <p className="inline-flex items-center gap-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-[var(--brand)]">
            <i className="w-5 h-px bg-[var(--brand)]" aria-hidden /> {kicker}
          </p>

          <h1 className="mt-4 text-[38px] xl:text-[44px] leading-[1.08] font-bold tracking-[-0.03em] text-[var(--text-primary)] text-balance">
            {headline}
          </h1>

          {lede && <p className="mt-4 text-[15px] leading-relaxed text-[var(--text-secondary)] max-w-[46ch]">{lede}</p>}

          {points && points.length > 0 && (
            <ul className="mt-8 flex flex-col gap-2.5">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[14px] text-[var(--text-secondary)]">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.4"
                       strokeLinecap="round" strokeLinejoin="round" className="mt-[3px] shrink-0" aria-hidden>
                    <path d="M4 12.5l5 5L20 6.5" />
                  </svg>
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* ── Правая колонка: форма ──────────────────────────────── */}
      <main className="flex-1 flex items-center justify-center px-5 py-10 sm:px-10">
        <div className="w-full max-w-[452px]">
          {/* На мобильном левой колонки нет — вордмарк переезжает сюда */}
          <Link href="https://logixa.one" className="lg:hidden inline-block mb-7">
            <span className="text-[21px] font-bold tracking-[-0.035em] text-[var(--text-primary)]">
              LOGI<span className="text-[var(--brand)]">X</span>A
            </span>
          </Link>
          {children}
        </div>
      </main>
    </div>
  );
}

/** Карточка формы: рамка вместо тени — как все панели в приложении. */
export function AuthCard({ title, subtitle, children, footer }: {
  title: string; subtitle?: string; children: ReactNode; footer?: ReactNode;
}) {
  return (
    <section className="bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius-md)]">
      <header className="px-7 pt-7 pb-5">
        <h2 className="text-[21px] font-semibold tracking-[-0.018em] text-[var(--text-primary)]">{title}</h2>
        {subtitle && <p className="mt-1.5 text-[13.5px] text-[var(--text-muted)]">{subtitle}</p>}
      </header>
      <div className="px-7 pb-7 flex flex-col gap-[18px]">{children}</div>
      {footer && (
        <footer className="px-7 py-4 border-t border-[var(--border)] text-[13.5px] text-[var(--text-muted)]">
          {footer}
        </footer>
      )}
    </section>
  );
}

/** Поле формы: подпись, иконка, ошибка. Иконка не заменяет подпись. */
export function AuthField({
  label, icon: Icon, error, hint, children,
}: {
  label: string; icon?: LucideIcon;
  error?: string | null; hint?: string; children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium text-[var(--text-secondary)]">{label}</span>
      <span className="relative block">
        {Icon && (
          <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
        )}
        <span className={cx('block [&>input]:h-11 [&>select]:h-11 [&>input]:text-[14px] [&>select]:text-[14px]',
                            Icon && '[&>input]:pl-10 [&>select]:pl-10')}>{children}</span>
      </span>
      {error
        ? <span className="text-[12.5px] text-[var(--danger)]">{error}</span>
        : hint ? <span className="text-[12.5px] text-[var(--text-muted)]">{hint}</span> : null}
    </label>
  );
}

/** Разделитель «или» между основным и альтернативным способом входа. */
export function AuthDivider({ children }: { children: string }) {
  return (
    <div className="flex items-center gap-3 py-0.5">
      <i className="flex-1 h-px bg-[var(--border)]" aria-hidden />
      <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--text-muted)]">{children}</span>
      <i className="flex-1 h-px bg-[var(--border)]" aria-hidden />
    </div>
  );
}

/** Ссылки под карточкой: регистрация, восстановление, возврат на сайт. */
export function AuthLinks({ children }: { children: ReactNode }) {
  return <div className="mt-5 flex flex-col items-center gap-2 text-[13px] text-[var(--text-muted)]">{children}</div>;
}

export function AuthLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link
      href={href}
      className="text-[var(--brand)] hover:text-[var(--brand-hover)] hover:underline focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)] rounded-[var(--radius-xs)]"
    >
      {children}
    </Link>
  );
}
